import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import { PublicNavbar, PublicFooter } from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MapPin, Package, Globe, ChevronRight,
  Lock, Building2, Award, X, ArrowRight, Tag, Map, List, Filter, Bookmark
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const SearchMap = lazy(() => import('../components/SearchMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_SUGGESTIONS = [
  'Digital Printing', 'Graphic Design', 'Signage', 'LED Screens',
  'Furniture Rental', 'Flooring', 'Lighting', 'AV Equipment',
  'Carpet', 'Modular Walls', 'Counter Display', 'Storage Units',
  'Branded Merchandise', 'Uniform', 'Catering Equipment'
];

export default function PublicFindSuppliers() {
  const [searchParams] = useSearchParams();
  const { city: cityParam, fairSlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const fairFromSlug = fairSlug
    ? fairSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';

  const [product, setProduct] = useState(searchParams.get('product') || '');
  const [city, setCity] = useState(cityParam ? decodeURIComponent(cityParam) : (searchParams.get('city') || ''));
  const [country, setCountry] = useState(searchParams.get('country') || '');
  const [fair, setFair] = useState(fairFromSlug || searchParams.get('fair') || '');
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [viewMode, setViewMode] = useState('list');

  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // When city route param changes, update city state
  useEffect(() => {
    if (cityParam) setCity(decodeURIComponent(cityParam));
    if (fairSlug) setFair(fairSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
  }, [cityParam, fairSlug]);

  const fetchSuppliers = async (params = {}) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      const _product = params.product !== undefined ? params.product : product;
      const _city = params.city !== undefined ? params.city : city;
      const _country = params.country !== undefined ? params.country : country;
      const _fair = params.fair !== undefined ? params.fair : fair;
      const _q = params.q !== undefined ? params.q : q;
      if (_product) p.set('product', _product);
      if (_city) p.set('city', _city);
      if (_country) p.set('country', _country);
      if (_fair) p.set('fair', _fair);
      if (_q) p.set('q', _q);
      p.set('limit', '30');
      const res = await axios.get(`${API}/public/suppliers?${p.toString()}`);
      setSuppliers(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      setSuppliers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchSuppliers(); }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchSuppliers();
  };

  const handleSaveSearch = async () => {
    if (!user) { navigate('/contractor/login'); return; }
    const name = window.prompt('Bu aramaya bir ad verin:', product || q || city || 'Kaydedilen Arama');
    if (!name) return;
    try {
      await axios.post(`${API}/saved-searches`,
        { name, search_type: 'supplier', filters: { q, product, city, country, fair } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Arama kaydedildi!', { description: 'Kayıtlı Aramalar sayfasından erişebilirsiniz.' });
    } catch { toast.error('Arama kaydedilemedi'); }
  };

  const activeFilters = [
    product && { key: 'product', label: product },
    city && { key: 'city', label: `📍 ${city}` },
    country && { key: 'country', label: `🌍 ${country}` },
    fair && { key: 'fair', label: `🎪 ${fair}` },
  ].filter(Boolean);

  const clearFilter = (field) => {
    if (field === 'product') { setProduct(''); fetchSuppliers({ product: '' }); }
    if (field === 'city') { setCity(''); fetchSuppliers({ city: '' }); }
    if (field === 'country') { setCountry(''); fetchSuppliers({ country: '' }); }
    if (field === 'fair') { setFair(''); fetchSuppliers({ fair: '' }); }
  };

  const pageTitle = fair
    ? `${fair} Exhibition Suppliers | ExpoStand`
    : product
    ? `${product} Suppliers for Exhibitions | ExpoStand`
    : city
    ? `Exhibition Suppliers in ${city.charAt(0).toUpperCase() + city.slice(1)} | ExpoStand`
    : 'Find Exhibition Suppliers | ExpoStand';

  const pageDesc = fair
    ? `Find verified exhibition suppliers for ${fair}. Furniture, printing, signage and more.`
    : product
    ? `Find verified ${product} suppliers for trade shows and exhibitions. Compare, request samples, and get quotes.`
    : city
    ? `Find ${total > 0 ? total + ' ' : ''}verified exhibition suppliers in ${city}. Furniture, printing, signage, AV equipment and more.`
    : 'Find and compare verified exhibition suppliers worldwide. Digital printing, furniture, lighting, signage and more.';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        {city && <link rel="canonical" href={`https://expostand.com/find-suppliers/${city.toLowerCase()}`} />}
      </Helmet>
      <PublicNavbar />

      {/* Hero Search Bar */}
      <div className="bg-gray-900 text-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Find Exhibition Suppliers</h1>
            <p className="text-gray-400 text-sm">
              {total > 0 ? `${total} suppliers found` : 'Search from our verified supplier network'}
              {city && ` in ${city}`}
              {product && ` for ${product}`}
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={product}
                  onChange={e => setProduct(e.target.value)}
                  placeholder="Product (e.g. Digital Printing)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                  list="product-suggestions"
                />
                <datalist id="product-suggestions">
                  {PRODUCT_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={city} onChange={e => setCity(e.target.value)}
                  placeholder="City (e.g. Istanbul)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={country} onChange={e => setCountry(e.target.value)}
                  placeholder="Country (e.g. Turkey)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={fair} onChange={e => setFair(e.target.value)}
                  placeholder="Fair (e.g. Hannover Messe)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Search by company name..."
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
              <button type="submit" className="px-8 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors text-sm shrink-0">
                Search
              </button>
            </div>
          </form>

          {/* Switch to Contractors */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-gray-500">Looking for a contractor instead?</span>
            <button onClick={() => navigate(`/find-contractors?service=${product}&city=${city}`)}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 font-medium">
              Find Contractors <ArrowRight className="h-3 w-3" />
            </button>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 hidden sm:inline">Popular fairs:</span>
              {['hannover-messe', 'ise-amsterdam', 'euromold', 'interpack-dusseldorf'].map(slug => {
                const label = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                return (
                  <button key={slug} onClick={() => navigate(`/find-suppliers/fair/${slug}`)}
                    className="text-[10px] text-purple-300 hover:text-white bg-white/10 hover:bg-white/20 px-2 py-1 rounded-full border border-white/20 transition-all">
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="max-w-5xl mx-auto flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-medium">Active filters:</span>
            {activeFilters.map(f => (
              <span key={f.key} className="flex items-center gap-1 bg-purple-50 text-purple-700 text-xs px-2.5 py-1 rounded-full border border-purple-200">
                {f.label}
                <button onClick={() => clearFilter(f.key)} className="hover:text-purple-900 ml-0.5">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="flex-1 py-6 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 animate-pulse h-48 shadow-sm" />
              ))}
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-20">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No suppliers found</h3>
              <p className="text-gray-500 text-sm mb-4">Try adjusting your search criteria</p>
              <button onClick={() => { setProduct(''); setCity(''); setCountry(''); setFair(''); setQ(''); fetchSuppliers({ product: '', city: '', country: '', fair: '', q: '' }); }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="text-sm text-gray-600 font-medium">{total} suppliers found</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Save Search button */}
                  <button
                    onClick={handleSaveSearch}
                    className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-purple-600 border border-gray-200 hover:border-purple-300 bg-white hover:bg-purple-50 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid="save-search-btn-supplier"
                    title="Bu aramayı kaydet"
                  >
                    <Bookmark className="h-3.5 w-3.5" />Kaydet
                  </button>
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button onClick={() => setViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'list' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                      <List className="h-3.5 w-3.5" /> List
                    </button>
                    <button onClick={() => setViewMode('map')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'map' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                      <Map className="h-3.5 w-3.5" /> Map
                    </button>
                    <button onClick={() => setViewMode('split')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'split' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                      <Filter className="h-3.5 w-3.5" /> Split
                    </button>
                  </div>
                  {!user && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5">
                      <Lock className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-xs text-amber-700 font-medium">Register to see contact details</span>
                      <Link to="/exhibitor/signup" className="text-xs text-blue-600 font-semibold hover:underline ml-1">Sign up free →</Link>
                    </div>
                  )}
                </div>
              </div>

              {viewMode === 'split' ? (
                <div className="flex gap-5">
                  <div className="w-full lg:w-1/2 space-y-3 overflow-y-auto" style={{ maxHeight: '620px' }}>
                    {suppliers.map(s => (
                      <div key={s.org_id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0 font-bold text-white text-sm">
                          {s.logo_url ? <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover rounded-lg" /> : s.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 text-xs line-clamp-1">{s.name}</h3>
                          <p className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <MapPin className="h-2.5 w-2.5" />{s.city && s.country ? `${s.city}, ${s.country}` : s.country || s.city || '—'}
                          </p>
                          <Link to={`/supplier/${s.slug}`} className="text-[10px] font-semibold text-purple-600 hover:underline">View →</Link>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden lg:block flex-1">
                    <Suspense fallback={<div className="h-full bg-gray-100 rounded-2xl animate-pulse" />}>
                      <SearchMap items={suppliers} type="supplier" height="620px" />
                    </Suspense>
                  </div>
                </div>
              ) : viewMode === 'map' ? (
                <Suspense fallback={<div className="h-96 bg-gray-100 rounded-2xl animate-pulse" />}>
                  <SearchMap items={suppliers} type="supplier" height="520px" />
                </Suspense>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {suppliers.map(s => (
                    <SupplierCard key={s.org_id} supplier={s} isLoggedIn={!!user} />
                  ))}
                </div>
              )}

              {/* Sign-up CTA */}
              {!user && suppliers.length > 0 && (
                <div className="mt-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
                  <Award className="h-10 w-10 mx-auto mb-3 opacity-90" />
                  <h3 className="text-xl font-bold mb-2">Connect With These Suppliers</h3>
                  <p className="text-purple-100 text-sm mb-5 max-w-md mx-auto">
                    Create a free account to view contact details and send inquiries directly.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/exhibitor/signup"
                      className="bg-white text-purple-700 font-bold px-6 py-3 rounded-xl hover:bg-purple-50 transition-colors text-sm">
                      Create Free Account
                    </Link>
                    <Link to="/supplier/signup"
                      className="border border-white/40 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors text-sm">
                      Join as Supplier
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}

function SupplierCard({ supplier: s, isLoggedIn }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-purple-100 transition-all group overflow-hidden">
      <div className="p-5 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-sm">
            {s.logo_url ? (
              <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover rounded-xl" />
            ) : (
              s.name.charAt(0)
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm leading-tight group-hover:text-purple-700 transition-colors line-clamp-2">
              {s.name}
            </h3>
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
              <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
              {s.city && s.country ? `${s.city}, ${s.country}` : s.country || s.city || '—'}
            </p>
          </div>
        </div>

        {s.tagline && (
          <p className="text-xs text-gray-500 italic line-clamp-2 mb-3">{s.tagline}</p>
        )}

        {/* Categories */}
        <div className="flex flex-wrap gap-1 mb-3">
          {(s.categories || []).slice(0, 3).map((cat, i) => (
            <span key={i} className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium border border-purple-100 flex items-center gap-0.5">
              <Tag className="h-2 w-2" />{cat}
            </span>
          ))}
          {(s.categories || []).length > 3 && (
            <span className="text-[10px] text-gray-400 px-1">+{s.categories.length - 3} more</span>
          )}
        </div>

        {/* Coverage */}
        {(s.coverage_countries || []).length > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-1">
            <Globe className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">Ships to: {s.coverage_countries.slice(0, 3).join(', ')}{s.coverage_countries.length > 3 ? ` +${s.coverage_countries.length - 3}` : ''}</span>
          </div>
        )}

        {/* Active Shows badges */}
        {s.active_shows?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {s.active_shows.slice(0, 2).map((show, i) => (
              <span key={i} className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold">
                ⭐ {show}
              </span>
            ))}
            {s.active_shows.length > 2 && <span className="text-[9px] text-gray-400">+{s.active_shows.length - 2}</span>}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-50 p-3 flex items-center gap-2">
        <Link
          to={`/supplier/${s.slug}`}
          className="flex-1 text-center text-xs font-semibold text-purple-600 hover:text-purple-700 py-2 rounded-lg hover:bg-purple-50 transition-colors"
        >
          View Profile
        </Link>
        {isLoggedIn ? (
          <Link
            to={`/supplier/${s.slug}`}
            className="flex-1 text-center text-xs font-semibold bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Get a Quote
          </Link>
        ) : (
          <Link
            to="/exhibitor/signup"
            className="flex-1 text-center text-xs font-semibold bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-1"
          >
            <Lock className="h-3 w-3" /> Contact
          </Link>
        )}
      </div>
    </div>
  );
}
